/**
 * Credit Block Case Queries
 *
 * Fetches cases via Gobodo Graph Traversal API:
 * - Per client: Client INCOMING → Case via case_client_rel (maxPathLength 1).
 * - Cases tab (Credit Block record type): resolve the Credit Block record_type object by Name__c,
 *   then traverse Record Type INCOMING → Case via record_type_rel, unioned with a zero-hop
 *   Case traversal on RecordTypeId__c = record_type.sf_id so legacy cases still appear.
 */

import { useQuery, useQueries, type QueryClient } from '@tanstack/react-query';
import type { CreditBlockClient } from '../../types';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import {
  getCaseAssigneeRelKey,
  getCaseClientRelKey,
  getOwnerRelKey,
  getRecordTypeRelKey,
  getUnlockRequestCaseRelKey,
} from '../../config/gobodoAccountRelationshipKeys';
import { CASE_CACHE_OPTS, CREDIT_BLOCK_BULK_LIST_SIZE } from './creditBlockQueries';
import { makeCacheOpts, type GobodoCacheOpts } from '../gobodo/gobodoCacheConfig';
import { persistedCaseRecords } from '../mockData/cases';
import { getRecordsForObject } from '../recordStorageService';
import { convertFromGobodoRecord } from '../gobodo/gobodoRecordService';
import {
  findGraphUserByOwnerRelation,
  looksLikeGraphUserSfId,
  resolveGraphUserNamesBySfIds,
} from '../gobodo/gobodoUserSfIdLookup';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { shouldUseGobodo, USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { ListViewRecord } from '../../types/listView';
import { gobodoUserProvider } from '../providers/gobodoUserProvider';
import {
  fetchCreditBlockRecordTypeTargetCached,
  type CreditBlockRecordTypeTarget,
} from './creditBlockRecordTypeTarget';

const customSuffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CASE_OBJECT = `Case${customSuffix}`;
const UNLOCK_REQUEST_OBJECT = `Unlock_Request${customSuffix}`;
const OWNER_REL_FIELD_KEY = `OwnerRelField${customSuffix}`;
const CASE_ROOT_FETCH_SIZE = 300;
const CASE_ROOT_CACHE_TTL_MS = 60_000;
let caseRootCache: { rows: Record<string, unknown>[]; fetchedAt: number } | null = null;
const CASE_TABLE_CACHE_TAG = 'v1-table-projection';
const LINKED_CASE_CLIENT_CACHE_TAG = 'v2-name-projection-cached';
export const CREDIT_BLOCK_CASE_ROW_CLIENT_QUERY_KEY = 'case-row-client-lookup';
/**
 * Safety valve for the legacy root Case object fallback (`/graph/.../objects/type/Case__c?page=0&size=300`).
 * Keep the implementation available, but default it off so the main Cases tab stays traversal-first only.
 */
const ENABLE_CREDIT_BLOCK_CASE_ROOT_FETCH_FALLBACK = false;
const ACCOUNT_FIELDS = [
  'AccountId__c', `AccountRelField${customSuffix}`, 'AccountRelField__c',
  'Client__c', 'accountId', 'ClientRelField__c', 'custom__client'
];
const CASE_TABLE_DATA_FIELDS = [
  'CaseNumber__c',
  'caseNumber',
  'CaseNumber',
  'Subject__c',
  'subject',
  'Subject',
  'Status__c',
  'status',
  'Status',
  'Priority__c',
  'priority',
  'Priority',
  'Type__c',
  'type',
  'Type',
  'Sub_Type__c',
  'subType',
  'RecordTypeId__c',
  'recordTypeId',
  'RecordType__c',
  'recordType',
  'AccountId__c',
  `AccountRelField${customSuffix}`,
  'AccountRelField__c',
  'Client__c',
  'accountId',
  'ClientRelField__c',
  'custom__client',
  'AccountName__c',
  'accountName',
  'AccountName',
  'ProductName__c',
  'productName',
  'ProductName',
  'OwnerName__c',
  'ownerName',
  'Owner__c',
  'owner',
  'Owner',
  'OwnerId__c',
  `OwnerId${customSuffix}`,
  'OwnerRelField__c',
  'Assignee__c',
  'assignee',
  'CaseAssignee__c',
  'caseAssignee',
  'CreatedDate__c',
  'createdDate',
  'CreatedDate',
  'LastModifiedDate__c',
  'lastModifiedDate',
  'LastModifiedDate',
  'EndDate__c',
  'endDate',
  'Due_Date__c',
  'DueDate__c',
  'dueDate',
  'ActivityDate__c',
  'activityDate',
  'TargetDate__c',
  'targetDate',
].join(',');
const CASE_LINKED_CLIENT_DATA_FIELDS = [
  'AccountName__c',
  'Name__c',
  'name',
].join(',');

type GobodoReadProjectionOpts = GobodoCacheOpts & {
  objectSections?: string;
  dataFields?: string;
  maxObjectTypeFields?: number;
  includeExpandedFieldData?: boolean;
  includeRelatedMedia?: boolean;
};

function buildCaseProjectionOpts(
  cacheOpts: GobodoCacheOpts,
  dataFields: string
): GobodoReadProjectionOpts {
  return {
    ...cacheOpts,
    objectSections: 'data',
    dataFields,
    maxObjectTypeFields: 0,
    includeExpandedFieldData: false,
    includeRelatedMedia: false,
  };
}

function normalizeObjectListResult(result: unknown): unknown[] {
  if (Array.isArray(result)) return result;
  if (Array.isArray((result as { content?: unknown[] } | null | undefined)?.content)) {
    return ((result as { content?: unknown[] }).content ?? []);
  }
  if (Array.isArray((result as { items?: unknown[] } | null | undefined)?.items)) {
    return ((result as { items?: unknown[] }).items ?? []);
  }
  return [];
}

function extractAccountId(r: Record<string, unknown>): string | null {
  const data = (r.data as Record<string, unknown>) ?? r;
  const rel = (r as { relatedClientId?: unknown }).relatedClientId;
  if (rel != null) return String(rel).trim() || null;
  const fields = ACCOUNT_FIELDS;
  for (const f of fields) {
    const v = data[f] ?? (r as Record<string, unknown>)[f];
    if (v == null) continue;
    if (typeof v === 'object' && v !== null && 'relatedObject' in v) {
      const ro = (v as { relatedObject?: { id?: unknown; key?: unknown } }).relatedObject;
      if (ro?.key != null) return String(ro.key).trim();
      if (ro?.id != null) return String(ro.id).trim();
    }
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
}

function extractOwnerLookupToken(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object' && value !== null && 'relatedObject' in value) {
    const ro = (value as { relatedObject?: { key?: unknown; id?: unknown } }).relatedObject;
    const related = String(ro?.key ?? ro?.id ?? '').trim();
    return related;
  }
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' ? value.trim() : '';
}

function extractOwnerText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object' && value !== null && 'relatedObject' in value) {
    const ro = (value as { relatedObject?: { label?: unknown; name?: unknown; key?: unknown; id?: unknown } }).relatedObject;
    return String(ro?.label ?? ro?.name ?? ro?.key ?? ro?.id ?? '').trim();
  }
  if (typeof value === 'object' && value !== null) {
    const raw = value as Record<string, unknown>;
    const first = String(raw.FirstName ?? raw.firstName ?? '').trim();
    const last = String(raw.LastName ?? raw.lastName ?? '').trim();
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    return String(raw.Name ?? raw.name ?? raw.label ?? '').trim();
  }
  if (typeof value === 'number') return String(value);
  return '';
}

function extractOwnerSfId(r: Record<string, unknown>): string {
  const d = (r.data as Record<string, unknown>) ?? r;
  const ownerIdSuffixed = `OwnerId${customSuffix}`;
  const candidates = [
    d.OwnerId__c,
    r.OwnerId__c,
    d[ownerIdSuffixed],
    r[ownerIdSuffixed],
    d.Owner__c,
    r.Owner__c,
    d.owner,
    r.owner,
    d.Owner,
    r.Owner,
    d[OWNER_REL_FIELD_KEY],
    r[OWNER_REL_FIELD_KEY],
    d.OwnerRelField__c,
    r.OwnerRelField__c,
    d.owner_id,
    r.owner_id,
  ];
  for (const candidate of candidates) {
    const token = extractOwnerLookupToken(candidate);
    if (token && looksLikeGraphUserSfId(token)) return token;
  }
  return '';
}

function extractOwnerDisplay(r: Record<string, unknown>): string {
  const d = (r.data as Record<string, unknown>) ?? r;
  const candidates = [
    d.OwnerName__c,
    r.OwnerName__c,
    d.ownerName,
    r.ownerName,
    d.Owner__c,
    r.Owner__c,
    d.owner,
    r.owner,
    d.Owner,
    r.Owner,
  ];
  for (const candidate of candidates) {
    const owner = extractOwnerText(candidate);
    if (owner && !looksLikeGraphUserSfId(owner)) {
      return owner;
    }
  }
  return '';
}

export async function hydrateCreditBlockCaseOwners(cases: ListViewRecord[]): Promise<ListViewRecord[]> {
  if (cases.length === 0) return cases;
  const assigneeRelationMatches = await Promise.all(
    cases.map(async (row) => {
      const startObjectKey = String((row as { key?: unknown }).key ?? row.id ?? '').trim();
      if (!startObjectKey) return null;
      return findGraphUserByOwnerRelation({
        startObjectKey,
        relationshipType: getCaseAssigneeRelKey(),
      });
    })
  );
  const ownerRelationMatches = await Promise.all(
    cases.map(async (row, index) => {
      if (assigneeRelationMatches[index]?.displayName) return null;
      const startObjectKey = String((row as { key?: unknown }).key ?? row.id ?? '').trim();
      if (!startObjectKey) return null;
      return findGraphUserByOwnerRelation({
        startObjectKey,
        relationshipType: getOwnerRelKey(),
      });
    })
  );
  const ownerIds = Array.from(
    new Set(
      cases
        .map((row, index) =>
          assigneeRelationMatches[index] || ownerRelationMatches[index]
            ? ''
            : String((row as { ownerSfId?: unknown }).ownerSfId ?? '').trim()
        )
        .filter(Boolean)
    )
  );
  const namesBySfId = ownerIds.length > 0 ? await resolveGraphUserNamesBySfIds(ownerIds) : new Map<string, string>();
  return cases.map((row, index) => {
    const assigneeRelationMatch = assigneeRelationMatches[index];
    if (assigneeRelationMatch?.displayName) {
      return {
        ...row,
        owner: assigneeRelationMatch.displayName,
        ...(assigneeRelationMatch.sfId ? { ownerSfId: assigneeRelationMatch.sfId } : {}),
      };
    }
    const ownerRelationMatch = ownerRelationMatches[index];
    if (ownerRelationMatch?.displayName) {
      return {
        ...row,
        owner: ownerRelationMatch.displayName,
        ...(ownerRelationMatch.sfId ? { ownerSfId: ownerRelationMatch.sfId } : {}),
      };
    }
    const ownerSfId = String((row as { ownerSfId?: unknown }).ownerSfId ?? '').trim();
    if (!ownerSfId) return row;
    const owner = namesBySfId.get(ownerSfId) || namesBySfId.get(stripSuffix(ownerSfId));
    return owner ? { ...row, owner } : row;
  });
}

function caseOwnerNeedsHydration(row: ListViewRecord): boolean {
  const owner = String(row.owner ?? '').trim();
  const ownerSfId = String((row as { ownerSfId?: unknown }).ownerSfId ?? '').trim();
  if (!owner) return true;
  if (looksLikeGraphUserSfId(owner)) return true;
  return Boolean(ownerSfId && owner === ownerSfId);
}

function ownerHydrationChangedRows(before: ListViewRecord[], after: ListViewRecord[]): boolean {
  if (before.length !== after.length) return true;
  for (let i = 0; i < before.length; i += 1) {
    const beforeOwner = String(before[i]?.owner ?? '').trim();
    const afterOwner = String(after[i]?.owner ?? '').trim();
    const beforeOwnerSfId = String((before[i] as { ownerSfId?: unknown })?.ownerSfId ?? '').trim();
    const afterOwnerSfId = String((after[i] as { ownerSfId?: unknown })?.ownerSfId ?? '').trim();
    if (beforeOwner !== afterOwner || beforeOwnerSfId !== afterOwnerSfId) return true;
  }
  return false;
}

function queueCaseOwnerHydrationForList(
  queryClient: QueryClient | undefined,
  queryKey: readonly unknown[],
  cases: ListViewRecord[]
): void {
  if (!queryClient || cases.length === 0 || !cases.some(caseOwnerNeedsHydration)) return;
  void hydrateCreditBlockCaseOwners(cases)
    .then((hydratedCases) => {
      if (!ownerHydrationChangedRows(cases, hydratedCases)) return;
      queryClient.setQueryData(queryKey, (current: unknown) => {
        if (!Array.isArray(current)) return current;
        return hydratedCases;
      });
    })
    .catch(() => {
      // Leave rows rendered with raw owner values when background hydration fails.
    });
}

function queueCaseOwnerHydrationForPaginatedResult(
  queryClient: QueryClient | undefined,
  queryKey: readonly unknown[],
  result: CreditBlockCasesPaginatedResult
): void {
  if (!queryClient || result.cases.length === 0 || !result.cases.some(caseOwnerNeedsHydration)) return;
  void hydrateCreditBlockCaseOwners(result.cases)
    .then((hydratedCases) => {
      if (!ownerHydrationChangedRows(result.cases, hydratedCases)) return;
      queryClient.setQueryData(queryKey, (current: unknown) => {
        if (current == null || typeof current !== 'object') return current;
        const existing = current as CreditBlockCasesPaginatedResult;
        return {
          ...existing,
          cases: hydratedCases,
        };
      });
    })
    .catch(() => {
      // Leave rows rendered with raw owner values when background hydration fails.
    });
}

function toListViewRecord(r: Record<string, unknown>): ListViewRecord {
  const d = (r.data as Record<string, unknown>) ?? r;
  const keyFromRecord = String((r as { key?: unknown }).key ?? (d as { key?: unknown }).key ?? '').trim();
  const numericId = String(r.id ?? d.id ?? '').trim();
  const owner = extractOwnerDisplay(r);
  const ownerSfId = extractOwnerSfId(r);
  const caseNumber = String(d.CaseNumber__c ?? d.caseNumber ?? d.CaseNumber ?? '').trim() || numericId;
  return {
    id: numericId,
    caseNumber,
    numericObjectId: numericId,
    subject: (d.Subject__c ?? d.subject ?? d.Subject ?? '') as string,
    status: (d.Status__c ?? d.status ?? d.Status ?? '') as string,
    priority: (d.Priority__c ?? d.priority ?? d.Priority ?? '') as string,
    type: (d.Type__c ?? d.type ?? d.Type ?? '') as string,
    subType: (d.Sub_Type__c ?? d.subType ?? '') as string,
    recordTypeId: (d.RecordTypeId__c ?? d.recordTypeId ?? '') as string,
    recordTypeLabel: (d.RecordType__c ?? d.recordType ?? '') as string,
    accountId: extractAccountId(r) ?? (d.accountId as string),
    accountName: (d.AccountName__c ?? d.accountName ?? d.AccountName ?? '') as string,
    productName: (d.ProductName__c ?? d.productName ?? d.ProductName ?? '') as string,
    owner: (owner || ownerSfId) as string,
    createdDate: (d.CreatedDate__c ?? d.createdDate ?? d.CreatedDate ?? '') as string,
    lastModifiedDate: (d.LastModifiedDate__c ?? d.lastModifiedDate ?? d.LastModifiedDate ?? '') as string,
    ...d,
    ...(ownerSfId ? { ownerSfId } : {}),
    ...(keyFromRecord ? { key: keyFromRecord } : {}),
  } as ListViewRecord;
}

function normId(id: string | undefined): string {
  if (!id) return '';
  return String(id).trim();
}

const stripSuffix = (s: string) => s.replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');

function normalizeForMatch(id: string): string {
  const n = normId(id);
  return n ? stripSuffix(n) : '';
}

/** True when two client id/key values refer to the same Client (with or without Gobodo suffix). */
function sameClientKey(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const na = normId(a);
  const nb = normId(b);
  if (!na || !nb) return false;
  return na === nb || normalizeForMatch(a) === normalizeForMatch(b);
}

/**
 * True when a Task RelatedTo case id is in the User→Case assignee traversal result
 * (exact match or same id after stripping Gobodo field suffixes).
 */
export function creditBlockCaseIdMatchesAssignedSet(
  relatedCaseId: string | undefined,
  assignedCaseIds: readonly string[] | Iterable<string>
): boolean {
  const r = normId(relatedCaseId);
  if (!r) return false;
  const set = new Set(
    Array.from(assignedCaseIds, (id) => normId(id)).filter(Boolean)
  );
  if (set.size === 0) return false;
  if (set.has(r)) return true;
  const rNorm = normalizeForMatch(r);
  if (!rNorm) return false;
  return Array.from(set).some((id) => normalizeForMatch(id) === rNorm);
}

const CLIENT_OBJECT = `Client${customSuffix}`;
const LINKED_CLIENT_LOOKUP_TIMEOUT_MS = 4500;

/** Server-side cache for case traversal in seconds (gobodo-api-promise expects seconds). 180s to align with Credit Block cache. */
const CASE_TRAVERSAL_CACHE_DURATION_SECONDS = 180;
/** React Query staleTime in ms (3 min). */
const CASE_TRAVERSAL_STALE_MS = 180_000;
/** Case-row linked client display names are stable enough to cache longer and persist across reloads. */
const LINKED_CASE_CLIENT_CACHE_DURATION_SECONDS = 1800;
const LINKED_CASE_CLIENT_STALE_MS = 30 * 60 * 1000;
/** Gobodo cache for one page of Client → Case traversal (must include startIndex/count so pages do not collide). */
function caseTraversalPageCacheOpts(clientId: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-case-traversal', CASE_TABLE_CACHE_TAG, clientId, String(startIndex), String(count)],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}
function caseTraversalCountCacheOpts(clientId: string, startIndex: number, count: number): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-case-traversal-count', CASE_TABLE_CACHE_TAG, clientId, String(startIndex), String(count)],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}
function caseTraversalCacheOptsMulti(clientIds: string[]): GobodoCacheOpts {
  const key = clientIds.slice(0, 50).map(normId).filter(Boolean).sort().join(',');
  return makeCacheOpts(
    ['credit-block-case-traversal-multi', CASE_TABLE_CACHE_TAG, key || 'all'],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}

/** Bumped when Credit Block Cases-tab traversal shape changes (gobodo-api-promise cache isolation). */
const CREDIT_BLOCK_ROOT_CASE_TRAVERSAL_CACHE_TAG = 'v7-rt-union-projected';

function caseTraversalCacheOptsCreditBlockRoot(page: number, pageSize: number): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-case-traversal-rt-name', CREDIT_BLOCK_ROOT_CASE_TRAVERSAL_CACHE_TAG, String(page), String(pageSize)],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}

/** Bumped when assignee traversal filters change so gobodo-api-promise does not reuse pre-filter cache entries. */
const ASSIGNEE_CASE_TRAVERSAL_CACHE_TAG = 'v2-open-status';

function caseAssigneeTraversalPageCacheOpts(
  userObjectIdStr: string,
  startIndex: number,
  count: number
): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-case-traversal-assignee', ASSIGNEE_CASE_TRAVERSAL_CACHE_TAG, CASE_TABLE_CACHE_TAG, userObjectIdStr, String(startIndex), String(count)],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}

function caseAssigneeTraversalCountCacheOpts(
  userObjectIdStr: string,
  startIndex: number,
  count: number
): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-case-traversal-assignee-count', ASSIGNEE_CASE_TRAVERSAL_CACHE_TAG, CASE_TABLE_CACHE_TAG, userObjectIdStr, String(startIndex), String(count)],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}

function caseUnlockRequestsTraversalCacheOpts(caseKey: string): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-case-to-unlock-requests', caseKey.slice(0, 80)],
    CASE_TRAVERSAL_CACHE_DURATION_SECONDS
  );
}

/** Gobodo User→Case assignee traversal uses `startObjectId` (numeric id), not `startObjectKey`. */
function parseGobodoUserObjectIdForTraversal(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export type CaseLinkedClientInfo = {
  displayName: string;
  clientKey: string;
  clientNumericId: string;
};

const EMPTY_LINKED_CLIENT: CaseLinkedClientInfo = {
  displayName: '',
  clientKey: '',
  clientNumericId: '',
};

function isNumericObjectId(s: string): boolean {
  return /^\d+$/.test(normId(s));
}

function isMeaningfulToken(value: unknown): boolean {
  const s = String(value ?? '').trim();
  if (!s) return false;
  const normalized = s.toLowerCase();
  return !['-', '—', 'null', 'undefined', 'n/a', 'na', 'none'].includes(normalized);
}

/**
 * Case.accountId often stores a bare Salesforce Account id; Client rows in Gobodo are keyed as id + schema suffix
 * (same pattern as CyberSweep `lookupAccountDisplayName` / `getObjectByKey(sfId + suffix)`).
 */
/** Append Gobodo Client object key suffix when missing (e.g. SF id → id + customSuffix). Exported for Credit Block navigation. */
export function toClientGobodoObjectKey(raw: string): string {
  const s = normId(raw);
  if (!s) return '';
  const suffix = GobodoConfig.customSuffix();
  if (suffix && s.toLowerCase().endsWith(suffix.toLowerCase())) return s;
  if (/(?:__stg3_c|__stg_c|__dev_c|__c)$/i.test(s)) return s;
  return s + suffix;
}

/** Gobodo server cache for GET /object/{key} and GET by id (gobodo-api-promise opts.cachekey / cacheduration). */
function clientByAccountLookupGobodoCacheOpts(cacheSegment: string): GobodoCacheOpts {
  const id = normalizeForMatch(cacheSegment) || cacheSegment || 'unknown';
  return makeCacheOpts(
    ['credit-block-case-tab-client-by-lookup', LINKED_CASE_CLIENT_CACHE_TAG, id],
    LINKED_CASE_CLIENT_CACHE_DURATION_SECONDS
  );
}

function mapGobodoObjectToCaseLinkedClientInfo(gobodoRecord: unknown): CaseLinkedClientInfo {
  if (gobodoRecord == null || typeof gobodoRecord !== 'object') return { ...EMPTY_LINKED_CLIENT };
  const gr = gobodoRecord as Record<string, unknown>;
  const objectTypeKey = String(
    gr.objecttypekey ?? (gr.objecttype as { key?: string } | undefined)?.key ?? CLIENT_OBJECT
  );
  try {
    const rec = convertFromGobodoRecord(gr, objectTypeKey) as Record<string, unknown>;
    const data = (rec.data as Record<string, unknown>) ?? rec;
    const clientKey = String((rec as { key?: unknown }).key ?? '').trim();
    const clientNumericId = String(rec.id ?? data.id ?? '').trim();
    const displayName = String(
      rec.AccountName__c ??
        data.AccountName__c ??
        rec.Name__c ??
        data.Name__c ??
        rec.name ??
        data.name ??
        ''
    ).trim();
    return { displayName, clientKey, clientNumericId };
  } catch {
    return { ...EMPTY_LINKED_CLIENT };
  }
}

/**
 * Load the Client (or related account) row for the Case’s accountId using GraphObjectsApi:
 * - all-digit accountId → getObjectById (Gobodo numeric id)
 * - otherwise → getObjectByKey with `toClientGobodoObjectKey` (append `GobodoConfig.customSuffix()` when needed),
 *   then on 404 retry with the raw string in case it is already a full graph key.
 * Passes cachekey/cacheduration for Gobodo-side caching.
 */
export async function getCaseLinkedClientByAccountLookup(accountLookup: string): Promise<CaseLinkedClientInfo> {
  const raw = normId(accountLookup);
  if (!isMeaningfulToken(raw)) return { ...EMPTY_LINKED_CLIENT };
  const objectsApi = new GobodoApi.GraphObjectsApi(getGobodoClient());

  const getStatus = (e: unknown) =>
    (e as { response?: { status?: number }; status?: number })?.response?.status ??
    (e as { status?: number })?.status;

  if (isNumericObjectId(raw)) {
    const { cachekey, cacheduration } = clientByAccountLookupGobodoCacheOpts(`id:${raw}`);
    const opts = buildCaseProjectionOpts({ cachekey, cacheduration }, CASE_LINKED_CLIENT_DATA_FIELDS);
    try {
      const gobodoRecord = await withTimeout(
        retryWithBackoff(() => objectsApi.getObjectById(Number(raw), opts), {
          maxRetries: 2,
          initialDelayMs: 1000,
        }),
        LINKED_CLIENT_LOOKUP_TIMEOUT_MS,
        `getObjectById(${raw})`
      );
      return mapGobodoObjectToCaseLinkedClientInfo(gobodoRecord);
    } catch (e: unknown) {
      if (getStatus(e) === 404) return { ...EMPTY_LINKED_CLIENT };
      throw e;
    }
  }

  const primaryKey = toClientGobodoObjectKey(raw);
  const keysToTry = primaryKey === raw ? [raw] : [primaryKey, raw];

  for (const key of keysToTry) {
    const { cachekey, cacheduration } = clientByAccountLookupGobodoCacheOpts(`key:${key}`);
    const opts = buildCaseProjectionOpts({ cachekey, cacheduration }, CASE_LINKED_CLIENT_DATA_FIELDS);
    try {
      const gobodoRecord = await withTimeout(
        retryWithBackoff(() => objectsApi.getObjectByKey(key, opts), {
          maxRetries: 2,
          initialDelayMs: 1000,
        }),
        LINKED_CLIENT_LOOKUP_TIMEOUT_MS,
        `getObjectByKey(${key})`
      );
      return mapGobodoObjectToCaseLinkedClientInfo(gobodoRecord);
    } catch (e: unknown) {
      if (getStatus(e) === 404) continue;
      throw e;
    }
  }
  return { ...EMPTY_LINKED_CLIENT };
}

/**
 * One TanStack query per row (same order as the caller’s list): resolves a Client via
 * Case.accountId, Unlock_Request.clientId, or any id/key string using
 * getObjectByKey (suffix when needed) / getObjectById + Gobodo cache opts.
 */
export function useCreditBlockCaseLinkedClientQueries(clientAccountLookups: string[], enabled = true) {
  const suffixForQueryKey = GobodoConfig.customSuffix();
  return useQueries({
    queries: clientAccountLookups.map((lookup, rowIndex) => {
      const raw = normId(lookup);
      const queryNorm = raw
        ? normalizeForMatch(raw) || raw
        : (`no-account-id-row-${rowIndex}` as const);
      return {
        queryKey: ['credit-block', CREDIT_BLOCK_CASE_ROW_CLIENT_QUERY_KEY, LINKED_CASE_CLIENT_CACHE_TAG, suffixForQueryKey, queryNorm] as const,
        queryFn: () => getCaseLinkedClientByAccountLookup(lookup),
        enabled: USE_GOBODO_API && shouldUseGobodo() && enabled && !!raw && isMeaningfulToken(raw),
        staleTime: LINKED_CASE_CLIENT_STALE_MS,
        gcTime: LINKED_CASE_CLIENT_STALE_MS,
        retry: (failureCount: number, error: unknown) => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 504 || (status && status >= 500)) return false;
          return failureCount < 1;
        },
      };
    }),
  });
}

const CREDIT_BLOCK_CLIENTS_QUERY_KEY = ['credit-block', 'clients', customSuffix] as const;

function buildAccountIdToName(clientRecords: Record<string, unknown>[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of clientRecords) {
    const key = String((r as { key?: string }).key ?? r.id ?? '');
    const data = (r as { data?: Record<string, unknown> }).data ?? r;
    const name = String(
      r.AccountName__c ?? r.accountName ?? r.Name__c ?? r.name ?? r.label ??
      (data && typeof data === 'object' ? (data as Record<string, unknown>).Name__c ?? (data as Record<string, unknown>).name : '')
    ).trim();
    if (!name) continue;
    map.set(key, name);
    const norm = stripSuffix(key);
    if (norm) map.set(norm, name);
  }
  return map;
}

/** Build accountId -> accountName from cached Credit Block clients to avoid a separate Client fetch. */
function buildAccountIdToNameFromCachedClients(clients: CreditBlockClient[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of clients) {
    if (!c.id) continue;
    const name = (c.accountName ?? '').trim();
    map.set(c.id, name);
    const norm = stripSuffix(c.id);
    if (norm) map.set(norm, name);
  }
  return map;
}

/**
 * Optionally add endObjectFieldFilters for Credit Block case type (Type__c EQUALS).
 * Backend may not support endObjectFieldFilters on all traversals.
 *
 * TEMPORARY: index filter disabled — re-enable when Gobodo traversal + Type__c filter behaves as expected.
 */
function applyCaseTypeFilter(_input: InstanceType<typeof GobodoApi.GraphTraversalInput>): void {
  // try {
  //   const filter = new GobodoApi.IndexFilter();
  //   filter.setIndexName('Type__c');
  //   filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  //   filter.setValues(['Credit Block']);
  //   _input.setEndObjectFieldFilters([filter]);
  // } catch {
  //   // Backend may not support endObjectFieldFilters
  // }
}

/**
 * My Work assignee traversal: exclude terminal Case statuses at the graph layer (aligned with SF picklist / list view).
 * Uses NOT_EQUALS per value; backend should AND filters. Matches client `isCaseOpenForMyWork` (case-insensitive there).
 */
function applyAssigneeCaseOpenStatusFilters(input: InstanceType<typeof GobodoApi.GraphTraversalInput>): void {
  const terminal: string[] = ['Closed', 'Cancelled', 'Suspended'];
  try {
    const filters = terminal.map((value) => {
      const f = new GobodoApi.IndexFilter();
      f.setIndexName('Status__c');
      f.setFunction(GobodoApi.IndexFilter.FunctionEnum.NOT_EQUALS);
      f.setValues([value]);
      return f;
    });
    input.setEndObjectFieldFilters(filters);
  } catch {
    // Backend may not support endObjectFieldFilters on this traversal
  }
}

function parseCasesTraversalCount(raw: unknown): number {
  const obj = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (obj && typeof obj.count === 'number' && !Number.isNaN(obj.count)) return obj.count;
  if (obj && obj.data != null && typeof obj.data === 'object' && typeof (obj.data as Record<string, unknown>).count === 'number') {
    return (obj.data as { count: number }).count;
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  return 0;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

/** Empty array = no matches. Anything else non-array is treated as an API failure (no bulk Case fetch fallback). */
function requireTraverseObjectsArray(result: unknown): unknown[] {
  if (Array.isArray(result)) return result;
  throw new Error(
    result == null
      ? 'Case traversal returned no data.'
      : `Case traversal returned an invalid response (${typeof result}).`
  );
}

/**
 * Credit Block Cases tab primary traversal: Record Type → Case. Edge in schema is Case → Record Type (`record_type_rel`);
 * traverse INCOMING from the resolved Credit Block record_type object to Case.
 */
function buildCreditBlockRootToCaseTraversalInput(
  recordTypeObjectKey: string,
  pagination?: { startIndex: number; count: number },
  searchQuery?: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  const graphId = GobodoConfig.graph();
  input.setGraphId(graphId);
  input.setStartObjectKey(recordTypeObjectKey);
  input.setEndObjectTypes([CASE_OBJECT]);
  input.setRelationshipTypes([getRecordTypeRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setMaxPathLength(1);
  applyCaseTypeFilter(input);
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(pagination.count);
  }
  const q = String(searchQuery ?? '').trim();
  if (q) {
    try {
      input.setSearchQuery(q);
      input.setSearchGraphId(graphId);
    } catch {
      // Backend may not support traversal search.
    }
  }
  return input;
}

/**
 * Zero-hop Case traversal for legacy rows that only have RecordTypeId__c populated.
 */
function buildCaseTraversalInputByRecordTypeSfId(
  recordTypeSfId: string,
  pagination?: { startIndex: number; count: number },
  searchQuery?: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> | null {
  const input = new GobodoApi.GraphTraversalInput();
  const graphId = GobodoConfig.graph();
  input.setGraphId(graphId);
  input.setStartObjectTypes([CASE_OBJECT]);
  input.setEndObjectTypes([CASE_OBJECT]);
  input.setMaxPathLength(0);
  try {
    const rtFilter = new GobodoApi.IndexFilter();
    rtFilter.setIndexName('RecordTypeId__c');
    rtFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
    rtFilter.setValues([recordTypeSfId]);
    input.setStartObjectFieldFilters([rtFilter]);
  } catch {
    return null;
  }
  if (pagination) {
    input.setStartIndex(pagination.startIndex);
    input.setCount(pagination.count);
  }
  const q = String(searchQuery ?? '').trim();
  if (q) {
    try {
      input.setSearchQuery(q);
      input.setSearchGraphId(graphId);
    } catch {
      // Backend may not support traversal search.
    }
  }
  return input;
}

/**
 * Build the Credit Block root cases traversal:
 * - primary branch: resolved record_type object INCOMING → Case via record_type_rel
 * - union branch: Case maxPathLength 0 by RecordTypeId__c = record_type.sf_id
 * If record_type.sf_id is missing/invalid, degrade to relationship-only traversal.
 */
function buildCreditBlockUnionCaseTraversalInput(
  recordType: CreditBlockRecordTypeTarget,
  pagination?: { startIndex: number; count: number },
  searchQuery?: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = buildCreditBlockRootToCaseTraversalInput(recordType.objectKey, pagination, searchQuery);
  if (!recordType.sfId) return input;
  const unionInput = buildCaseTraversalInputByRecordTypeSfId(recordType.sfId, pagination, searchQuery);
  if (!unionInput) return input;
  input.setUnionWith(unionInput);
  return input;
}

/** Paginated cases for Credit Block record type (resolved Record Type start → Case traversal with unioned RecordTypeId__c branch). */
async function getCasesViaCreditBlockRootTraversalPaginated(
  queryClient: QueryClient,
  page: number,
  pageSize: number,
  searchQuery?: string,
  skipOwnerHydration = false
): Promise<{ cases: ListViewRecord[]; totalCount: number }> {
  const recordType = await fetchCreditBlockRecordTypeTargetCached(queryClient);
  if (!recordType?.objectKey) {
    return { cases: [], totalCount: 0 };
  }
  const startIndex = (page - 1) * pageSize;
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const traverseInput = buildCreditBlockUnionCaseTraversalInput(recordType, { startIndex, count: pageSize }, searchQuery);
  const countInput = buildCreditBlockUnionCaseTraversalInput(recordType, undefined, searchQuery);
  const cacheOpts = caseTraversalCacheOptsCreditBlockRoot(page, pageSize);
  const pageReadOpts = buildCaseProjectionOpts(cacheOpts, CASE_TABLE_DATA_FIELDS);

  const [result, countRaw] = await Promise.all([
    retryWithBackoff(() => traversalApi.traverseObjects(traverseInput, pageReadOpts), { maxRetries: 2, initialDelayMs: 1000 }),
    retryWithBackoff(() => traversalApi.traverseObjectsCount(countInput, cacheOpts), { maxRetries: 2, initialDelayMs: 1000 }),
  ]);
  const objects = requireTraverseObjectsArray(result);
  const totalCount = parseCasesTraversalCount(countRaw);
  const list: ListViewRecord[] = [];
  for (const gobodoObj of objects) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, CASE_OBJECT) as Record<string, unknown>;
      list.push(toListViewRecord(rec));
    } catch {
      // skip malformed record
    }
  }
  return { cases: skipOwnerHydration ? list : await hydrateCreditBlockCaseOwners(list), totalCount };
}

function buildCaseTraversalInputForClient(
  clientId: string,
  startIndex?: number,
  count?: number,
  searchQuery?: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  const graphId = GobodoConfig.graph();
  input.setGraphId(graphId);
  input.setStartObjectKey(clientId);
  input.setRelationshipTypes([getCaseClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([CASE_OBJECT]);
  input.setMaxPathLength(1);
  if (typeof startIndex === 'number' && typeof count === 'number') {
    input.setStartIndex(startIndex);
    input.setCount(count);
  }
  applyCaseTypeFilter(input);
  const q = String(searchQuery ?? '').trim();
  if (q) {
    try {
      input.setSearchQuery(q);
      input.setSearchGraphId(graphId);
    } catch {
      // Backend may not support traversal search.
    }
  }
  return input;
}

/**
 * User (numeric object id) → Credit Block Case via case_assignee_rel (Case→User edge; INCOMING from User reaches Case).
 * API expects `startObjectId`, not `startObjectKey`.
 */
function buildCaseTraversalInputForAssigneeUser(
  userObjectId: number,
  startIndex?: number,
  count?: number
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectId(userObjectId);
  input.setRelationshipTypes([getCaseAssigneeRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([CASE_OBJECT]);
  input.setMaxPathLength(1);
  if (typeof startIndex === 'number' && typeof count === 'number') {
    input.setStartIndex(startIndex);
    input.setCount(count);
  }
  applyCaseTypeFilter(input);
  applyAssigneeCaseOpenStatusFilters(input);
  return input;
}

function parseCaseTraverseArray(result: unknown): ListViewRecord[] {
  if (!result || !Array.isArray(result)) return [];
  const list: ListViewRecord[] = [];
  for (const gobodoObj of result) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, CASE_OBJECT) as Record<string, unknown>;
      list.push(toListViewRecord(rec));
    } catch {
      // skip malformed record
    }
  }
  return list;
}

/** One page of Client → Case traversal. Returns null if the API response is not an array (caller may treat as failure). */
async function traverseCasesForClientPage(
  clientId: string,
  startIndex: number,
  count: number
): Promise<ListViewRecord[] | null> {
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const input = buildCaseTraversalInputForClient(clientId, startIndex, count);
  const cacheOpts = caseTraversalPageCacheOpts(clientId, startIndex, count);
  const pageReadOpts = buildCaseProjectionOpts(cacheOpts, CASE_TABLE_DATA_FIELDS);
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, pageReadOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  if (!result || !Array.isArray(result)) return null;
  return parseCaseTraverseArray(result);
}

/**
 * All cases for one client via paged traversal (CREDIT_BLOCK_BULK_LIST_SIZE per request).
 * Avoids a single traverse with the backend default count (often 100).
 */
async function getCasesViaTraversal(clientId: string, skipOwnerHydration = false): Promise<ListViewRecord[]> {
  const pageSize = CREDIT_BLOCK_BULK_LIST_SIZE;
  const maxPages = 40;
  const merged: ListViewRecord[] = [];
  for (let p = 0; p < maxPages; p++) {
    const startIndex = p * pageSize;
    const page = await traverseCasesForClientPage(clientId, startIndex, pageSize);
    if (page === null) {
      if (p === 0) return [];
      break;
    }
    merged.push(...page);
    if (page.length < pageSize) break;
  }
  return skipOwnerHydration ? merged : hydrateCreditBlockCaseOwners(merged);
}

/** Fetch one page of cases for one client via traversal (startIndex/count + traverseObjectsCount). Cached by TanStack Query and gobodo-api-promise. */
async function getCasesViaTraversalPaginated(
  clientId: string,
  page: number,
  pageSize: number,
  searchQuery?: string,
  skipOwnerHydration = false
): Promise<{ cases: ListViewRecord[]; totalCount: number }> {
  const startIndex = (page - 1) * pageSize;
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const traverseInput = buildCaseTraversalInputForClient(clientId, startIndex, pageSize, searchQuery);
  const countInput = buildCaseTraversalInputForClient(clientId, undefined, undefined, searchQuery);
  const pageCacheOpts = caseTraversalPageCacheOpts(clientId, startIndex, pageSize);
  const countCacheOpts = caseTraversalCountCacheOpts(clientId, startIndex, pageSize);
  const pageReadOpts = buildCaseProjectionOpts(pageCacheOpts, CASE_TABLE_DATA_FIELDS);

  const [result, countRaw] = await Promise.all([
    retryWithBackoff(() => traversalApi.traverseObjects(traverseInput, pageReadOpts), { maxRetries: 2, initialDelayMs: 1000 }),
    retryWithBackoff(() => traversalApi.traverseObjectsCount(countInput, countCacheOpts), { maxRetries: 2, initialDelayMs: 1000 }),
  ]);
  requireTraverseObjectsArray(result);
  const totalCount = parseCasesTraversalCount(countRaw);
  const list = parseCaseTraverseArray(result);
  return { cases: skipOwnerHydration ? list : await hydrateCreditBlockCaseOwners(list), totalCount };
}

/** Paginated Credit Block cases assigned to a Gobodo User (numeric User `startObjectId`), via User→Case traversal. */
export async function getCreditBlockCasesAssignedToGraphUserPaginated(
  gobodoUserObjectId: string,
  page: number,
  pageSize: number
): Promise<CreditBlockCasesPaginatedResult> {
  const userObjectId = parseGobodoUserObjectIdForTraversal(gobodoUserObjectId);
  if (userObjectId == null) return { cases: [], totalCount: 0 };
  const idStr = String(userObjectId);
  const startIndex = (page - 1) * pageSize;
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const traverseInput = buildCaseTraversalInputForAssigneeUser(userObjectId, startIndex, pageSize);
  const countInput = buildCaseTraversalInputForAssigneeUser(userObjectId);
  const pageCacheOpts = caseAssigneeTraversalPageCacheOpts(idStr, startIndex, pageSize);
  const countCacheOpts = caseAssigneeTraversalCountCacheOpts(idStr, startIndex, pageSize);
  const pageReadOpts = buildCaseProjectionOpts(pageCacheOpts, CASE_TABLE_DATA_FIELDS);

  const [result, countRaw] = await Promise.all([
    retryWithBackoff(() => traversalApi.traverseObjects(traverseInput, pageReadOpts), { maxRetries: 2, initialDelayMs: 1000 }),
    retryWithBackoff(() => traversalApi.traverseObjectsCount(countInput, countCacheOpts), { maxRetries: 2, initialDelayMs: 1000 }),
  ]);
  requireTraverseObjectsArray(result);
  const totalCount = parseCasesTraversalCount(countRaw);
  const list = parseCaseTraverseArray(result);
  return { cases: await hydrateCreditBlockCaseOwners(list), totalCount };
}

const ASSIGNEE_CASES_PAGE_SIZE = 50;
const ASSIGNEE_CASES_MAX_PAGES = 20;

/** Numeric User id for `GraphTraversalInput.startObjectId` from `getSelf()` (id may be number or digit string). */
export function userObjectIdStringForAssigneeTraversal(
  self: { id?: unknown; graphObjectKey?: string | undefined } | null | undefined
): string | null {
  if (self == null) return null;
  const rawId = self.id;
  if (typeof rawId === 'number' && Number.isFinite(rawId) && rawId >= 0) {
    return String(Math.trunc(rawId));
  }
  const s = String(rawId ?? '').trim();
  if (/^\d+$/.test(s)) return s;
  const gk = String(self.graphObjectKey ?? '').trim();
  if (/^\d+$/.test(gk)) return gk;
  return null;
}

function assigneeCaseDedupeKey(c: ListViewRecord): string {
  const row = c as ListViewRecord & { key?: string };
  const cid = normId(String(c.id ?? ''));
  const ckey = normId(String(row.key ?? ''));
  const primary = cid || ckey;
  if (!primary) return '';
  const nk = normalizeForMatch(primary);
  return nk || primary;
}

/**
 * All Case rows for the user via User→Case assignee traversal (`case_assignee_rel`, INCOMING from User).
 * De-duplicates by id/key (including suffix-normalized match).
 */
export async function getAllCreditBlockCasesAssignedToGraphUser(gobodoUserObjectId: string): Promise<ListViewRecord[]> {
  if (parseGobodoUserObjectIdForTraversal(gobodoUserObjectId) == null || !USE_GOBODO_API) return [];
  const byKey = new Map<string, ListViewRecord>();
  for (let p = 0; p < ASSIGNEE_CASES_MAX_PAGES; p++) {
    const { cases } = await getCreditBlockCasesAssignedToGraphUserPaginated(gobodoUserObjectId, p + 1, ASSIGNEE_CASES_PAGE_SIZE);
    if (cases.length === 0) break;
    for (const c of cases) {
      const k = assigneeCaseDedupeKey(c);
      if (k && !byKey.has(k)) byKey.set(k, c);
    }
    if (cases.length < ASSIGNEE_CASES_PAGE_SIZE) break;
  }
  return Array.from(byKey.values());
}

/**
 * My Work: `getSelf()` then User→Case assignee `traverseObjects` — full Case rows for dashboard lists.
 */
export async function fetchCreditBlockAssignedCasesViaSelf(): Promise<ListViewRecord[]> {
  if (!USE_GOBODO_API || !shouldUseGobodo()) return [];
  const self = await gobodoUserProvider.getSelf();
  let idStr = userObjectIdStringForAssigneeTraversal(self);
  if (!idStr) {
    const env = process.env.REACT_APP_GOBODO_GRAPH_USER_KEY?.trim() ?? '';
    if (/^\d+$/.test(env)) idStr = env;
  }
  if (!idStr) return [];
  return getAllCreditBlockCasesAssignedToGraphUser(idStr);
}

/** Parse ISO/date string for My Work sorting and Due Soon; invalid → null. */
export function parseCaseDateForMyWork(value: string | undefined | null): Date | null {
  if (value == null || !String(value).trim()) return null;
  const d = new Date(String(value).trim());
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Best-effort case due date from common Gobodo / SF field aliases. */
export function caseDueDate(c: ListViewRecord): string {
  const r = c as Record<string, unknown>;
  const v =
    r.EndDate__c ??
    r.endDate ??
    r.Due_Date__c ??
    r.DueDate__c ??
    r.dueDate ??
    r.ActivityDate__c ??
    r.activityDate ??
    r.TargetDate__c ??
    r.targetDate ??
    '';
  return String(v ?? '').trim();
}

/** Open for My Work queue: not Closed, Cancelled, or Suspended (aligned with assignee traversal filters). */
export function isCaseOpenForMyWork(c: ListViewRecord): boolean {
  const s = String(c.status ?? (c as Record<string, unknown>).Status__c ?? '').trim();
  if (!s) return true;
  const lower = s.toLowerCase();
  return lower !== 'closed' && lower !== 'cancelled' && lower !== 'suspended';
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse unlock/block window bounds from Unlock_Request row (aligned with createUnlockRequestRecordsForClient). */
export function parseUnlockRequestDueBounds(r: Record<string, unknown>): { start: Date | null; end: Date | null } {
  const d = (r.data as Record<string, unknown>) ?? r;
  const endRaw = String(
    d.End_Date__c ?? d.Unlock_End_Date__c ?? d.endDate ?? r.End_Date__c ?? r.Unlock_End_Date__c ?? ''
  ).trim();
  const startRaw = String(
    d.Start_Date__c ?? d.Unlock_Start_Date__c ?? d.startDate ?? r.Start_Date__c ?? r.Unlock_Start_Date__c ?? ''
  ).trim();
  const end = endRaw ? new Date(endRaw) : null;
  const start = startRaw ? new Date(startRaw) : null;
  return {
    end: end && !Number.isNaN(end.getTime()) ? end : null,
    start: start && !Number.isNaN(start.getTime()) ? start : null,
  };
}

/** True if any linked unlock request has start or end date in [today, today+windowDays] (local midnight). */
export function unlockRequestsDueSoonInWindow(rows: ListViewRecord[], windowDays = 3): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const last = new Date(today.getTime() + windowDays * MS_PER_DAY);
  for (const row of rows) {
    const { start, end } = parseUnlockRequestDueBounds(row as Record<string, unknown>);
    for (const dt of [end, start]) {
      if (!dt) continue;
      const x = new Date(dt);
      x.setHours(0, 0, 0, 0);
      if (x >= today && x <= last) return true;
    }
  }
  return false;
}

/** Case INCOMING → Unlock_Request via `unlock_request_case_rel` (Unlock_Request → Case in schema). */
export async function fetchUnlockRequestsLinkedToCase(caseObjectKeyOrId: string): Promise<ListViewRecord[]> {
  const start = String(caseObjectKeyOrId ?? '').trim();
  if (!start || !USE_GOBODO_API || !shouldUseGobodo()) return [];
  try {
    const apiClient = getGobodoClient();
    const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(GobodoConfig.graph());
    input.setStartObjectKey(start);
    input.setRelationshipTypes([getUnlockRequestCaseRelKey()]);
    input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
    input.setEndObjectTypes([UNLOCK_REQUEST_OBJECT]);
    input.setMaxPathLength(1);
    const cacheOpts = caseUnlockRequestsTraversalCacheOpts(start);
    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input, cacheOpts),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const objects = requireTraverseObjectsArray(result);
    const list: ListViewRecord[] = [];
    for (const gobodoObj of objects) {
      try {
        const rec = convertFromGobodoRecord(gobodoObj, UNLOCK_REQUEST_OBJECT) as Record<string, unknown>;
        list.push(rec as ListViewRecord);
      } catch {
        /* skip */
      }
    }
    return list;
  } catch {
    return [];
  }
}

/** Due-soon using unlock request dates, else Case `caseDueDate` when no UR rows. */
export function caseDueSoonForMyWork(
  unlockRows: ListViewRecord[],
  caseRow: ListViewRecord,
  windowDays = 3
): boolean {
  if (unlockRows.length > 0) return unlockRequestsDueSoonInWindow(unlockRows, windowDays);
  const dueRaw = caseDueDate(caseRow);
  if (!dueRaw) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const last = new Date(today.getTime() + windowDays * MS_PER_DAY);
  const d = new Date(dueRaw);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(0, 0, 0, 0);
  return d >= today && d <= last;
}

/** Fetch cases for multiple clients via traversal (all Clients INCOMING → Case), then filter to clientIds. Cached by TanStack Query and gobodo-api-promise. */
async function getCasesViaTraversalForClients(clientIds: string[], skipOwnerHydration = false): Promise<ListViewRecord[]> {
  const ids = new Set(clientIds.map(normId).filter(Boolean));
  const normalizedIds = new Set(clientIds.map(normalizeForMatch).filter(Boolean));
  const matches = (c: ListViewRecord) => {
    const aid = normId(c.accountId);
    if (!aid) return false;
    return ids.has(aid) || normalizedIds.has(normalizeForMatch(aid));
  };

  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([CLIENT_OBJECT]);
  input.setRelationshipTypes([getCaseClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([CASE_OBJECT]);
  input.setMaxPathLength(1);
  applyCaseTypeFilter(input);
  const cacheOpts = caseTraversalCacheOptsMulti(clientIds);
  const pageReadOpts = buildCaseProjectionOpts(cacheOpts, CASE_TABLE_DATA_FIELDS);

  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, pageReadOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  const objects = requireTraverseObjectsArray(result);

  const list: ListViewRecord[] = [];
  for (const gobodoObj of objects) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, CASE_OBJECT) as Record<string, unknown>;
      const entry = toListViewRecord(rec);
      if (matches(entry)) list.push(entry);
    } catch {
      // skip malformed record
    }
  }
  return skipOwnerHydration ? list : hydrateCreditBlockCaseOwners(list);
}

/** Fetch one page of cases (all clients) via traversal with startIndex/count; filter result by clientIds. Cached by TanStack Query and gobodo-api-promise. */
async function getCasesViaTraversalForClientsPaginated(
  clientIds: string[],
  page: number,
  pageSize: number,
  searchQuery?: string,
  skipOwnerHydration = false
): Promise<{ cases: ListViewRecord[]; totalCount: number }> {
  const ids = new Set(clientIds.map(normId).filter(Boolean));
  const normalizedIds = new Set(clientIds.map(normalizeForMatch).filter(Boolean));
  const matches = (c: ListViewRecord) => {
    const aid = normId(c.accountId);
    if (!aid) return false;
    return ids.has(aid) || normalizedIds.has(normalizeForMatch(aid));
  };

  const startIndex = (page - 1) * pageSize;
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const graphId = GobodoConfig.graph();
  const buildInput = (pagination?: { startIndex: number; count: number }) => {
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(graphId);
    input.setStartObjectTypes([CLIENT_OBJECT]);
    input.setRelationshipTypes([getCaseClientRelKey()]);
    input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
    input.setEndObjectTypes([CASE_OBJECT]);
    input.setMaxPathLength(1);
    if (pagination) {
      input.setStartIndex(pagination.startIndex);
      input.setCount(pagination.count);
    }
    applyCaseTypeFilter(input);
    const q = String(searchQuery ?? '').trim();
    if (q) {
      try {
        input.setSearchQuery(q);
        input.setSearchGraphId(graphId);
      } catch {
        // Backend may not support traversal search.
      }
    }
    return input;
  };
  const traverseInput = buildInput({ startIndex, count: pageSize });
  const countInput = buildInput();
  const cacheOpts = caseTraversalCacheOptsMulti(clientIds);
  const pageReadOpts = buildCaseProjectionOpts(cacheOpts, CASE_TABLE_DATA_FIELDS);

  const [result, countRaw] = await Promise.all([
    retryWithBackoff(() => traversalApi.traverseObjects(traverseInput, pageReadOpts), { maxRetries: 2, initialDelayMs: 1000 }),
    retryWithBackoff(() => traversalApi.traverseObjectsCount(countInput, cacheOpts), { maxRetries: 2, initialDelayMs: 1000 }),
  ]);
  const objects = requireTraverseObjectsArray(result);
  const totalCount = parseCasesTraversalCount(countRaw);
  const list: ListViewRecord[] = [];
  for (const gobodoObj of objects) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, CASE_OBJECT) as Record<string, unknown>;
      const entry = toListViewRecord(rec);
      if (matches(entry)) list.push(entry);
    } catch {
      // skip malformed record
    }
  }
  return { cases: skipOwnerHydration ? list : await hydrateCreditBlockCaseOwners(list), totalCount };
}

/** Fallback: fetch Case records and filter by clientIds. Optionally skips bulk Client fetch when `knownClient` matches the only requested id (client detail page). */
async function getCasesViaFetchAndFilter(
  clientIds: string[],
  matches: (c: ListViewRecord) => boolean,
  enrichOpts?: { knownClient?: Pick<CreditBlockClient, 'id' | 'accountName'> }
): Promise<ListViewRecord[]> {
  const caseRecords = await getRecordsForObject(CASE_OBJECT, { size: CREDIT_BLOCK_BULK_LIST_SIZE, cache: CASE_CACHE_OPTS });
  let accountIdToName: Map<string, string>;
  const kc = enrichOpts?.knownClient;
  if (clientIds.length === 1 && kc?.id && sameClientKey(clientIds[0], kc.id)) {
    accountIdToName = buildAccountIdToNameFromCachedClients([
      { id: clientIds[0], accountName: kc.accountName ?? '' } as CreditBlockClient,
    ]);
  } else {
    accountIdToName = new Map<string, string>();
  }
  const list = (caseRecords as Record<string, unknown>[]).map(toListViewRecord);
  const filtered = list.filter((c) => matches(c)).map((c) => {
    const aid = c.accountId ?? (c as { AccountId__c?: string }).AccountId__c ?? '';
    const accountName = c.accountName || accountIdToName.get(aid) || accountIdToName.get(stripSuffix(aid)) || '';
    return { ...c, accountName: accountName || c.accountName };
  });
  return hydrateCreditBlockCaseOwners(filtered);
}

async function getCasesViaRootFetchFallbackPaginated(
  page: number,
  pageSize: number,
  searchQuery?: string
): Promise<CreditBlockCasesPaginatedResult> {
  const now = Date.now();
  const cachedRows = caseRootCache?.rows ?? [];
  const hasCachedRows = cachedRows.length > 0;
  const cacheAgeMs = caseRootCache ? now - caseRootCache.fetchedAt : Number.POSITIVE_INFINITY;
  const shouldRefresh =
    !hasCachedRows ||
    (cacheAgeMs > CASE_ROOT_CACHE_TTL_MS &&
      page === 1 &&
      String(searchQuery ?? '').trim().length === 0);

  let records = cachedRows;
  if (shouldRefresh) {
    const objectsApi = new GobodoApi.GraphObjectsApi(getGobodoClient());
    const fetched = await retryWithBackoff(
      () =>
        objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), CASE_OBJECT, {
          page: 0,
          size: CASE_ROOT_FETCH_SIZE,
          ...buildCaseProjectionOpts(
            makeCacheOpts(['credit-block-case-root-objects', CASE_TABLE_CACHE_TAG], CASE_TRAVERSAL_CACHE_DURATION_SECONDS),
            CASE_TABLE_DATA_FIELDS
          ),
        }),
      { maxRetries: 2, initialDelayMs: 1000 }
    )
      .then((result) =>
        normalizeObjectListResult(result).map((record) => convertFromGobodoRecord(record, CASE_OBJECT) as Record<string, unknown>)
      )
      .catch(() => [] as Record<string, unknown>[]);
    if (fetched.length > 0) {
      records = fetched as Record<string, unknown>[];
      caseRootCache = { rows: records, fetchedAt: now };
    } else if (!hasCachedRows) {
      records = [];
    }
  }
  let list = (records as Record<string, unknown>[]).map(toListViewRecord);
  const q = String(searchQuery ?? '').trim().toLowerCase();
  if (q) {
    list = list.filter((c) => {
      const haystack = [
        c.caseNumber,
        c.subject,
        c.status,
        c.priority,
        c.type,
        c.subType,
        c.accountName,
        c.assignee,
        c.owner,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ');
      return haystack.includes(q);
    });
  }
  const resolveSortMs = (row: ListViewRecord) => {
    const raw = row as Record<string, unknown>;
    const candidates = [
      row.createdDate,
      raw.CreatedDate__c,
      raw.createdDate,
      raw.created_date,
      raw.Request_Date__c,
      raw.requestDate,
      row.lastModifiedDate,
      raw.LastModifiedDate__c,
      raw.lastModifiedDate,
    ];
    for (const c of candidates) {
      const ms = Date.parse(String(c ?? ''));
      if (Number.isFinite(ms)) return ms;
    }
    return 0;
  };
  list.sort((a, b) => {
    const ta = resolveSortMs(a);
    const tb = resolveSortMs(b);
    if (tb !== ta) return tb - ta;
    // Preserve source order when timestamps are equal/missing.
    return 0;
  });
  const start = Math.max(0, (page - 1) * pageSize);
  return { cases: list.slice(start, start + pageSize), totalCount: list.length };
}

function getCasesViaRootPersistedFallbackPaginated(
  page: number,
  pageSize: number,
  searchQuery?: string
): CreditBlockCasesPaginatedResult {
  const q = String(searchQuery ?? '').trim().toLowerCase();
  let list = persistedCaseRecords.get().slice();
  if (q) {
    list = list.filter((c) => {
      const haystack = [
        c.caseNumber,
        c.subject,
        c.status,
        c.priority,
        c.type,
        c.subType,
        c.accountName,
        c.assignee,
        c.owner,
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ');
      return haystack.includes(q);
    });
  }
  const resolveMs = (row: ListViewRecord) => {
    const raw = row as Record<string, unknown>;
    const candidates = [
      row.createdDate,
      raw.CreatedDate__c,
      raw.createdDate,
      raw.created_date,
      raw.Request_Date__c,
      raw.requestDate,
      row.lastModifiedDate,
      raw.LastModifiedDate__c,
      raw.lastModifiedDate,
    ];
    for (const c of candidates) {
      const ms = Date.parse(String(c ?? ''));
      if (Number.isFinite(ms)) return ms;
    }
    return 0;
  };
  list.sort((a, b) => {
    const ta = resolveMs(a);
    const tb = resolveMs(b);
    if (tb !== ta) return tb - ta;
    // Preserve source order when timestamps are equal/missing.
    return 0;
  });
  const start = Math.max(0, (page - 1) * pageSize);
  return { cases: list.slice(start, start + pageSize), totalCount: list.length };
}

async function resolveCaseAccountNameMap(
  queryClient: QueryClient | undefined,
  ids: Set<string>,
  clientIds: string[],
  knownClient?: Pick<CreditBlockClient, 'id' | 'accountName'>
): Promise<Map<string, string>> {
  if (ids.size === 1 && knownClient?.id) {
    const onlyId = normId(clientIds[0]);
    if (onlyId && sameClientKey(onlyId, knownClient.id)) {
      return buildAccountIdToNameFromCachedClients([
        { id: clientIds[0], accountName: knownClient.accountName ?? '' } as CreditBlockClient,
      ]);
    }
  }
  const cachedClients = queryClient?.getQueryData(CREDIT_BLOCK_CLIENTS_QUERY_KEY) as CreditBlockClient[] | undefined;
  if (cachedClients?.length) {
    return buildAccountIdToNameFromCachedClients(cachedClients);
  }
  return new Map<string, string>();
}

export interface UseCreditBlockCasesOptions {
  /**
   * When loading cases for this client only (e.g. Credit Block client detail), pass their display name
   * so we do not call `getRecordsForObject(Client)` to enrich rows (avoids a bulk Client list fetch).
   */
  knownClient?: Pick<CreditBlockClient, 'id' | 'accountName'>;
}

/**
 * Cases for Credit Block operations page (filtered to clients in clientIds).
 * Enriches with accountName from Client records unless `knownClient` is provided for a single-client view.
 */
export function useCreditBlockCases(
  clientIds: string[],
  enabled = true,
  options?: UseCreditBlockCasesOptions
) {
  const ids = new Set(clientIds.map(normId).filter(Boolean));
  const normalizedIds = new Set(clientIds.map(normalizeForMatch).filter(Boolean));
  const knownClient = options?.knownClient;
  const queryKey = ['credit-block', 'cases', Array.from(ids).sort().join(',')] as const;

  return useQuery({
    queryKey,
    queryFn: async ({ client }): Promise<ListViewRecord[]> => {
      const matches = (c: ListViewRecord) => {
        const aid = normId(c.accountId);
        if (!aid) return false;
        return ids.has(aid) || normalizedIds.has(normalizeForMatch(aid));
      };
      if (shouldUseGobodo()) {
        let list: ListViewRecord[];
        let fromFallback = false;
        try {
          if (ids.size === 1) {
            list = await getCasesViaTraversal(clientIds[0], true);
          } else {
            list = await getCasesViaTraversalForClients(clientIds, true);
          }
          if (list.length === 0) {
            const skipBulkCaseFetch =
              ids.size === 1 && knownClient?.id && sameClientKey(clientIds[0], knownClient.id);
            if (!skipBulkCaseFetch) {
              list = await getCasesViaFetchAndFilter(clientIds, matches, { knownClient });
              fromFallback = true;
            }
          }
        } catch {
          list = await getCasesViaFetchAndFilter(clientIds, matches, { knownClient });
          fromFallback = true;
        }
        // Enrich accountName when list came from traversal; avoid bulk Client fetch when we already know this client (detail page).
        if (!fromFallback) {
          const accountIdToName = await resolveCaseAccountNameMap(client, ids, clientIds, knownClient);
          const rows = list.map((c) => {
            const aid = c.accountId ?? (c as { AccountId__c?: string }).AccountId__c ?? '';
            const accountName = c.accountName || accountIdToName.get(aid) || accountIdToName.get(stripSuffix(aid)) || '';
            return { ...c, accountName: accountName || c.accountName };
          });
          queueCaseOwnerHydrationForList(client, queryKey, rows);
          return rows;
        }
        queueCaseOwnerHydrationForList(client, queryKey, list);
        return list;
      }
      const all = persistedCaseRecords.get();
      return all.filter((c) => matches(c));
    },
    staleTime: CASE_TRAVERSAL_STALE_MS,
    enabled: USE_GOBODO_API && enabled && ids.size > 0,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 504 || (status && status >= 500)) return false;
      return failureCount < 1;
    },
  });
}

export interface CreditBlockCasesPaginatedResult {
  cases: ListViewRecord[];
  totalCount: number;
}

const DEFAULT_CASES_PAGE_SIZE = 10;

async function getCasesByExactCaseNumber(searchQuery: string): Promise<ListViewRecord[]> {
  const q = String(searchQuery ?? '').trim();
  if (!q) return [];
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const graphId = GobodoConfig.graph();
  const inputs: InstanceType<typeof GobodoApi.GraphTraversalInput>[] = [];

  const buildInput = (indexName: string) => {
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(graphId);
    input.setStartObjectTypes([CASE_OBJECT]);
    input.setEndObjectTypes([CASE_OBJECT]);
    input.setMaxPathLength(0);
    input.setCount(50);
    const filter = new GobodoApi.IndexFilter();
    filter.setIndexName(indexName);
    filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
    filter.setValues([q]);
    input.setStartObjectFieldFilters([filter]);
    return input;
  };

  inputs.push(buildInput('CaseNumber__c'));
  inputs.push(buildInput('case_number'));

  const out: ListViewRecord[] = [];
  const seen = new Set<string>();
  for (const input of inputs) {
    try {
      const rows = await retryWithBackoff(
        () => traversalApi.traverseObjects(input, { cachekey: ['cb-cases-exact-case-number', q], cacheduration: '120' }),
        { maxRetries: 1, initialDelayMs: 800 }
      );
      if (!Array.isArray(rows)) continue;
      for (const gobodoObj of rows) {
        try {
          const rec = convertFromGobodoRecord(gobodoObj, CASE_OBJECT) as Record<string, unknown>;
          const row = toListViewRecord(rec);
          const idKey = `${row.id ?? ''}|${(row as { key?: string }).key ?? ''}`;
          if (!seen.has(idKey)) {
            seen.add(idKey);
            out.push(row);
          }
        } catch {
          // skip malformed record
        }
      }
    } catch {
      // continue to alternate index
    }
  }
  return hydrateCreditBlockCaseOwners(out);
}

export interface UseCreditBlockCasesPaginatedOptions {
  /**
   * When true and `clientIds` is empty, load cases via Record Type (Name__c filter) INCOMING → Case on `record_type_rel`.
   * Used by the Credit Block Cases tab.
   */
  creditBlockRootCases?: boolean;
  /**
   * Single-client detail: fill `accountName` on rows without bulk Client fetches (same as `useCreditBlockCases`).
   */
  knownClient?: Pick<CreditBlockClient, 'id' | 'accountName'>;
}

/**
 * Server-side paginated Credit Block cases via traversal (startIndex/count + traverseObjectsCount).
 * Sends one traversal request per page; no client-side fetch-all-then-slice.
 */
export function useCreditBlockCasesPaginated(
  clientIds: string[],
  page: number,
  pageSize: number,
  enabled = true,
  options?: UseCreditBlockCasesPaginatedOptions,
  searchQuery?: string
) {
  const ids = clientIds.map(normId).filter(Boolean);
  const useCreditBlockRoot = Boolean(options?.creditBlockRootCases && ids.length === 0);
  const queryKey = [
    'credit-block',
    'cases',
    'paginated',
    CASE_TABLE_CACHE_TAG,
    useCreditBlockRoot ? 'cb-root-rt-name-v3' : ids.sort().join(','),
    page,
    pageSize,
    String(searchQuery ?? '').trim(),
  ] as const;

  return useQuery({
    queryKey,
    queryFn: async ({ client }): Promise<CreditBlockCasesPaginatedResult> => {
      if (!shouldUseGobodo()) {
        const all = persistedCaseRecords.get();
        const normalizedIds = new Set(clientIds.map(normalizeForMatch).filter(Boolean));
        const matches = (c: ListViewRecord) => {
          if (ids.length === 0 && useCreditBlockRoot) return true;
          const aid = normId(c.accountId);
          return Boolean(aid && (ids.includes(aid) || normalizedIds.has(normalizeForMatch(aid))));
        };
        const filtered = all.filter((c) => matches(c));
        const start = Math.max(0, (page - 1) * pageSize);
        return { cases: filtered.slice(start, start + pageSize), totalCount: filtered.length };
      }
      let result: { cases: ListViewRecord[]; totalCount: number };
      try {
        if (useCreditBlockRoot) {
          result = await getCasesViaCreditBlockRootTraversalPaginated(client, page, pageSize, searchQuery, true).catch(
            () => ({ cases: [], totalCount: 0 } as CreditBlockCasesPaginatedResult)
          );
          if (ENABLE_CREDIT_BLOCK_CASE_ROOT_FETCH_FALLBACK && result.totalCount === 0) {
            result = await getCasesViaRootFetchFallbackPaginated(page, pageSize, searchQuery);
          }
          // Do not fall back to persisted mock data when Gobodo API is active
        } else if (ids.length === 1) {
          result = await getCasesViaTraversalPaginated(ids[0], page, pageSize, searchQuery, true);
        } else {
          result = await getCasesViaTraversalForClientsPaginated(clientIds, page, pageSize, searchQuery, true);
        }
      } catch {
        if (useCreditBlockRoot) {
          if (ENABLE_CREDIT_BLOCK_CASE_ROOT_FETCH_FALLBACK) {
            result = await getCasesViaRootFetchFallbackPaginated(page, pageSize, searchQuery);
          } else {
            result = { cases: [], totalCount: 0 };
          }
        } else {
          const matches = (c: ListViewRecord) => {
            const aid = normId(c.accountId);
            if (!aid) return false;
            const normSet = new Set(clientIds.map(normalizeForMatch).filter(Boolean));
            return ids.includes(aid) || normSet.has(normalizeForMatch(aid));
          };
          const fallback = await getCasesViaFetchAndFilter(clientIds, matches);
          const start = Math.max(0, (page - 1) * pageSize);
          result = { cases: fallback.slice(start, start + pageSize), totalCount: fallback.length };
        }
      }
      if (
        ENABLE_CREDIT_BLOCK_CASE_ROOT_FETCH_FALLBACK &&
        useCreditBlockRoot &&
        result.cases.length === 0 &&
        result.totalCount === 0
      ) {
        result = await getCasesViaRootFetchFallbackPaginated(page, pageSize, searchQuery);
      }
      // Optional in-memory names from React Query cache only — never bulk-fetch Client list here.
      let { cases, totalCount } = result;
      const cachedClients = client?.getQueryData(CREDIT_BLOCK_CLIENTS_QUERY_KEY) as CreditBlockClient[] | undefined;
      if (cachedClients?.length) {
        const accountIdToName = buildAccountIdToNameFromCachedClients(cachedClients);
        cases = cases.map((c) => {
          const aid = normId(c.accountId ?? (c as { AccountId__c?: string }).AccountId__c ?? '');
          const accountName =
            (c.accountName && String(c.accountName).trim()) ||
            (aid ? accountIdToName.get(aid) || accountIdToName.get(stripSuffix(aid)) : '') ||
            '';
          return accountName ? { ...c, accountName } : c;
        });
      }
      const kc = options?.knownClient;
      if (kc?.id && ids.length === 1 && sameClientKey(clientIds[0], kc.id)) {
        const name = (kc.accountName ?? '').trim();
        if (name) {
          cases = cases.map((c) => ({
            ...c,
            accountName: (c.accountName && String(c.accountName).trim()) || name,
          }));
        }
      }
      const finalResult = { cases, totalCount };
      queueCaseOwnerHydrationForPaginatedResult(client, queryKey, finalResult);
      return finalResult;
    },
    enabled:
      page >= 1 &&
      pageSize >= 1 &&
      enabled &&
      (USE_GOBODO_API ? ids.length > 0 || useCreditBlockRoot : true),
    staleTime: CASE_TRAVERSAL_STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 504 || (status && status >= 500)) return false;
      return failureCount < 1;
    },
  });
}

/**
 * My Work assignee cases: runs `getSelf()` inside the query, then `traverseObjects` with `startObjectId` from that user.
 * Returns full Case rows (`getCaseAssigneeRelKey()` traversal). Single loading state covers both steps.
 */
export function useCreditBlockAssignedCasesForMyWork(enabled = true) {
  return useQuery({
    queryKey: ['credit-block', 'cases', 'my-work-assignee-cases-from-self', 'open-status-v2'] as const,
    queryFn: fetchCreditBlockAssignedCasesViaSelf,
    enabled: Boolean(enabled && USE_GOBODO_API && shouldUseGobodo()),
    staleTime: CASE_TRAVERSAL_STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 504 || (status && status >= 500)) return false;
      return failureCount < 1;
    },
  });
}

export {
  DEFAULT_CASES_PAGE_SIZE,
  getCasesViaCreditBlockRootTraversalPaginated,
};
